import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { TFunction } from 'i18next'
import { z } from 'zod'

import { withinRunes } from '~/pages/AdminOcctoMaster/tabs/_common/schema'

/**
 * 入力の文字数上限 (名称・略称は広域機関システムの桁数に合わせる。
 * `docs/domain/models/plant-unit.md`)。前後の空白を除いた文字数で数える。
 */
export const GENERATION_SITE_NAME_MAX_LENGTH = 100
export const GENERATION_SITE_NAME_ABBR_MAX_LENGTH = 50
export const GENERATION_SITE_NOTE_MAX_LENGTH = 100

/**
 * 発電所 (サイト) の登録・編集フォームの schema。必須は名称とエリア。
 * 名称・略称・備考は前後の空白を除いた値を扱う (backend の entity も同じ)。
 * 名称の一意は backend が保存時に判定する (AlreadyExists)。
 */
// [規約差異] schemaは表示言語の自然文を返す。日本語はja辞書、英語はspec付録を正とする。
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- zodの合成型は推論に任せる
export const buildGenerationSiteFormSchema = (t: TFunction) =>
  z.object({
    siteName: z
      .string()
      .trim()
      .min(1, t('page:adminPlantUnitMaster.site.validation.nameRequired'))
      .refine(
        withinRunes(GENERATION_SITE_NAME_MAX_LENGTH),
        t('page:adminPlantUnitMaster.site.validation.nameTooLong'),
      ),
    siteNameAbbr: z
      .string()
      .trim()
      .refine(
        withinRunes(GENERATION_SITE_NAME_ABBR_MAX_LENGTH),
        t('page:adminPlantUnitMaster.site.validation.abbrTooLong'),
      ),
    // proto は not_in: [0] を課すため UNSPECIFIED を弾く (nativeEnum だけでは 0 が通る)。
    areaCode: z
      .nativeEnum(AreaCode, {
        error: () =>
          t('page:adminPlantUnitMaster.site.validation.areaRequired'),
      })
      .refine(
        (v) => v !== AreaCode.UNSPECIFIED,
        t('page:adminPlantUnitMaster.site.validation.areaRequired'),
      ),
    note: z
      .string()
      .trim()
      .refine(
        withinRunes(GENERATION_SITE_NOTE_MAX_LENGTH),
        t('page:adminPlantUnitMaster.site.validation.noteTooLong'),
      ),
  })

export type GenerationSiteFormValues = z.output<
  ReturnType<typeof buildGenerationSiteFormSchema>
>
export type GenerationSiteFormInput = z.input<
  ReturnType<typeof buildGenerationSiteFormSchema>
>

/** 未入力の略称・備考は送らない (absent)。 */
export function toOptionalText(value: string): string | undefined {
  return value === '' ? undefined : value
}
